import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Avoid circular dependency by defining a subset of the state type here
interface RootStateSubset {
  auth: {
    accessToken: string | null;
    refreshToken: string | null;
  };
}

// Create a base query instance for API calls
const baseQuery = fetchBaseQuery({
  baseUrl: (import.meta.env.VITE_API_BASE_URL as string) || 'https://contentcreator-api-dev.wejha.com/api/v1',
  prepareHeaders: (headers, { getState }) => {
    // Auto header injection
    const token = (getState() as unknown as RootStateSubset).auth.accessToken;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    headers.set('accept', 'application/json');
    return headers;
  },
});

/**
 * Custom base query that handles automatic token refresh on 401 Unauthorized errors.
 */
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, apiInstance, extraOptions) => {
  let result = await baseQuery(args, apiInstance, extraOptions);

  if (result.error && result.error.status === 401) {
    console.log('Token expired, attempting refresh...');
    
    // Get refresh token from state
    const state = apiInstance.getState() as RootStateSubset;
    const refreshToken = state.auth.refreshToken;

    if (refreshToken) {
      // Try to get a new access token
      const refreshResult: any = await baseQuery(
        {
          url: '/auth/refresh-tokens',
          method: 'POST',
          body: { refreshToken },
        },
        apiInstance,
        extraOptions
      );

      if (refreshResult.data) {
        console.log('Token refreshed successfully, retrying request...');
        const data = refreshResult.data.data || refreshResult.data;
        
        // Break circular dependency: Use direct dispatch with action types 
        // instead of importing setCredentials from authSlice
        apiInstance.dispatch({
            type: 'auth/setCredentials',
            payload: {
                user: data.user,
                accessToken: data.tokens.access.token,
                refreshToken: data.tokens.refresh.token,
            }
        });

        // Retry the initial query with the new token
        result = await baseQuery(args, apiInstance, extraOptions);
      } else {
        console.warn('Refresh failed, logging out');
        apiInstance.dispatch({ type: 'auth/logout' });
      }
    } else {
      apiInstance.dispatch({ type: 'auth/logout' });
    }
  }
  return result;
};

// Centralized API slice
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'Auth', 'Templates', 'SavedTemplates', 'Assets', 'BrandKits', 'CustomApi'],
  endpoints: () => ({}),
});
